/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiContextMenuItem,
  EuiContextMenuPanel,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiIcon,
  EuiPanel,
  EuiPopover,
  EuiSearchBar,
  EuiSkeletonText,
  EuiSpacer,
  Query,
  SearchFilterConfig,
} from "@inscopix/ideas-eui";
import { Fragment, useState } from "react";
import { ContainerCard } from "./ContainerCard";
import { FlyoutContainerInfo } from "./FlyoutContainerInfo";
import { useFlyoutContext } from "providers/FlyoutProvider/FlyoutProvider";
import { ContainerDjango, useAllContainersQuery } from "./ContainerBrowser.api";
import { chain } from "lodash";
import { getTagColor } from "./Tag";
import { isNonNull } from "utils/isNonNull";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { CallOutError } from "components/CallOutError/CallOutError";
import { PromptFirstCustomContainer } from "./PromptFirstCustomContainer";
import { VList } from "virtua";

type Sort =
  | "created-asc"
  | "created-desc"
  | "updated-asc"
  | "updated-desc"
  | "name-asc";

interface SortFilterProps {
  sort: Sort;
  setSort: (sort: Sort) => void;
}

const SortFilter = ({ sort, setSort }: SortFilterProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const items: { name: string; value: Sort }[] = [
    { name: "Newest", value: "created-desc" },
    { name: "Oldest", value: "created-asc" },
    { name: "Recently updated", value: "updated-desc" },
    { name: "Least recently updated", value: "updated-asc" },
    { name: "Name", value: "name-asc" },
  ];

  return (
    <EuiPopover
      style={{ borderLeft: "1px solid #d7dee8" }}
      isOpen={isPopoverOpen}
      closePopover={() => setIsPopoverOpen(false)}
      button={
        <EuiButtonEmpty
          color="text"
          iconSide="right"
          iconType="arrowDown"
          onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
        >
          Sort
        </EuiButtonEmpty>
      }
    >
      <EuiContextMenuPanel
        size="s"
        items={items.map((item) => (
          <EuiContextMenuItem
            key={item.value}
            onClick={() => setSort(item.value)}
            icon={item.value === sort ? "check" : "empty"}
          >
            {item.name}
          </EuiContextMenuItem>
        ))}
      />
    </EuiPopover>
  );
};

type Scope = "custom" | "all";

interface ScopeFilterProps {
  scope: Scope;
  setScope: (scope: Scope) => void;
}

const ScopeFilter = ({ scope, setScope }: ScopeFilterProps) => {
  return (
    <EuiButtonGroup
      legend="Scope filter"
      options={[
        {
          id: "all",
          label: "All",
          css: css`
            border-start-end-radius: 0px !important;
            border-end-end-radius: 0px !important;
          `,
        },
        { id: "custom", label: "Custom" },
      ]}
      idSelected={scope}
      onChange={(scope) => setScope(scope as Scope)}
      buttonSize="m"
    />
  );
};

const styles = {
  root: css`
    height: 100%;
    padding: 24px;
  `,
};

interface ContainerBrowserProps {
  tenantId: number;
  tenantKey: string;
}

export const ContainerBrowser = ({
  tenantId,
  tenantKey,
}: ContainerBrowserProps) => {
  const { data, isLoading, error } = useAllContainersQuery();
  const { flyout, setFlyout, closeFlyout } = useFlyoutContext();
  const [query, setQuery] = useState<Query>();
  const [scope, setScope] = useState<Scope>("all");
  const [sort, setSort] = useState<Sort>("created-desc");
  const isCustomContainer = data?.some(
    (container) => container.tenant?.id === tenantId,
  );
  const [selectedContainerId, setSelectedContainerId] = useState<number>();
  const selectedContainer = (data ?? []).find(
    (c) => c.id === selectedContainerId,
  );

  const containers = (() => {
    let containers = data ?? [];

    if (query) {
      containers = Query.execute(query, containers);
    }

    return containers
      .filter((container) => {
        switch (scope) {
          case "custom":
            return container.tenant?.id === tenantId;
          default:
            return true;
        }
      })
      .sort((a, b) => {
        switch (sort) {
          case "name-asc":
            if (a.name < b.name) {
              return -1;
            } else if (a.name > b.name) {
              return 1;
            } else {
              return 0;
            }
          case "created-asc":
            return (
              new Date(a.date_created).getTime() -
              new Date(b.date_created).getTime()
            );
          case "created-desc":
            return (
              new Date(b.date_created).getTime() -
              new Date(a.date_created).getTime()
            );
          case "updated-asc":
            return (
              new Date(a.date_updated).getTime() -
              new Date(b.date_updated).getTime()
            );
          case "updated-desc":
            return (
              new Date(b.date_updated).getTime() -
              new Date(a.date_updated).getTime()
            );
        }
        // ESLint array-callback-return doesn't detect exhaustive switch cases
        return 0;
      });
  })();

  const searchFilters: SearchFilterConfig[] = [
    {
      type: "custom_component",
      component: () => <ScopeFilter scope={scope} setScope={setScope} />,
    },
    {
      type: "field_value_selection",
      field: "user" satisfies keyof ContainerDjango,
      name: "Author",
      multiSelect: "or",
      options: chain(data ?? [])
        .map((c) => c.user)
        .filter(isNonNull)
        .uniq()
        .map((author) => {
          return {
            value: author,
            name: author,
            view: <UserAvatar userId={author} size="s" displayName />,
          };
        })
        .value(),
    },
    {
      type: "field_value_selection",
      field: "tags" satisfies keyof ContainerDjango,
      name: "Tag",
      multiSelect: "or",
      options: chain(data ?? [])
        .flatMap((c) => c.tags)
        .filter(isNonNull)
        .uniq()
        .map((tag) => {
          return {
            value: tag,
            name: tag,
            view: <EuiHealth color={getTagColor(tag)}>{tag}</EuiHealth>,
          };
        })
        .value(),
    },
    {
      type: "custom_component",
      component: () => <SortFilter sort={sort} setSort={setSort} />,
    },
  ];

  return (
    <>
      {selectedContainer !== undefined && flyout === "containerInfo" && (
        <FlyoutContainerInfo
          {...selectedContainer}
          onClose={() => {
            setSelectedContainerId(undefined);
            closeFlyout();
          }}
        />
      )}

      <EuiFlexGroup css={styles.root} direction="column" gutterSize="l">
        <EuiFlexItem grow={false}>
          <EuiSearchBar
            query={query}
            onChange={({ query }) => setQuery(query ?? undefined)}
            filters={searchFilters}
            box={{
              placeholder: "Find a container...",
              incremental: false,
            }}
          />
        </EuiFlexItem>

        <EuiFlexItem style={{ overflow: "hidden" }}>
          {(() => {
            if (isLoading) {
              return <EuiSkeletonText lines={5} />;
            } else if (error) {
              return <CallOutError />;
            } else if (scope === "custom" && !isCustomContainer) {
              return <PromptFirstCustomContainer />;
            } else if (containers.length === 0) {
              return (
                <EuiPanel hasBorder>
                  <EuiEmptyPrompt
                    icon={<EuiIcon type="indexMapping" size="xl" />}
                    title={<h2>No containers found</h2>}
                    titleSize="m"
                  />
                </EuiPanel>
              );
            } else {
              return (
                <VList style={{ height: "100%" }}>
                  {containers.map((container, idx, arr) => {
                    const isLastItem = idx === arr.length - 1;
                    return (
                      <Fragment key={container.id}>
                        <ContainerCard
                          {...container}
                          onClick={() => {
                            setSelectedContainerId(container.id);
                            setFlyout("containerInfo");
                          }}
                        />
                        {!isLastItem && <EuiSpacer />}
                      </Fragment>
                    );
                  })}
                </VList>
              );
            }
          })()}
        </EuiFlexItem>
      </EuiFlexGroup>
    </>
  );
};
